'use client';

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import {
  UserRole,
  UserPermissions,
  LoginRequest,
  LoginResponse,
  ChangePasswordRequest,
  ChangePasswordResponse,
  InspecDoorModule,
  CRUDAction,
  ROLE_PERMISSIONS,
  FrontendUser
} from '@/types/auth';

interface AuthContextType {
  user: FrontendUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  permissions: UserPermissions;
  
  login: (credentials: LoginRequest) => Promise<LoginResponse>;
  logout: () => Promise<void>;
  changePassword: (request: ChangePasswordRequest) => Promise<ChangePasswordResponse>;
  refreshSession: () => Promise<void>;
  
  hasPermission: (module: InspecDoorModule, action?: CRUDAction) => boolean;
  hasRole: (requiredRole: UserRole) => boolean;
  canAccess: (path: string) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<FrontendUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [permissions, setPermissions] = useState<UserPermissions>(ROLE_PERMISSIONS.monteur);

  const isAuthenticated = !!user;

  const updatePermissions = useCallback((userRole: UserRole) => {
    setPermissions(ROLE_PERMISSIONS[userRole]);
  }, []);

  const hasPermission = useCallback((module: InspecDoorModule, action?: CRUDAction): boolean => {
    if (!user) return false;

    switch (module) {
      case 'dashboard':
        return permissions.canRead;
      case 'kunden':
        return permissions.canAccessKunden && (!action || permissions[`can${action.charAt(0).toUpperCase() + action.slice(1)}` as keyof UserPermissions] as boolean);
      case 'lieferorte':
        return permissions.canAccessLieferorte && (!action || permissions[`can${action.charAt(0).toUpperCase() + action.slice(1)}` as keyof UserPermissions] as boolean);
      case 'standorte':
        return permissions.canAccessStandorte && (!action || permissions[`can${action.charAt(0).toUpperCase() + action.slice(1)}` as keyof UserPermissions] as boolean);
      case 'tueren':
        return permissions.canAccessTueren && (!action || permissions[`can${action.charAt(0).toUpperCase() + action.slice(1)}` as keyof UserPermissions] as boolean);
      case 'pruefungen':
        return permissions.canAccessPruefungen && (!action || permissions[`can${action.charAt(0).toUpperCase() + action.slice(1)}` as keyof UserPermissions] as boolean);
      case 'reparaturen':
        return permissions.canAccessReparaturen && (!action || permissions[`can${action.charAt(0).toUpperCase() + action.slice(1)}` as keyof UserPermissions] as boolean);
      case 'berichte':
        return permissions.canAccessBerichte && (!action || permissions[`can${action.charAt(0).toUpperCase() + action.slice(1)}` as keyof UserPermissions] as boolean);
      case 'nutzer':
        return permissions.canManageUsers;
      case 'sync':
        return permissions.canManageUsers || permissions.canAccessSettings;
      case 'einstellungen':
        return permissions.canAccessSettings;
      default:
        return false;
    }
  }, [user, permissions]);

  const hasRole = useCallback((requiredRole: UserRole): boolean => {
    if (!user) return false;

    const roleHierarchy: Record<UserRole, number> = {
      'monteur': 1,
      'verwaltung': 2,
      'admin': 3
    };

    return roleHierarchy[user.role] >= roleHierarchy[requiredRole];
  }, [user]);

  const canAccess = useCallback((path: string): boolean => {
    if (!user) return false;

    const pathToModule: Record<string, InspecDoorModule> = {
      '/': 'dashboard',
      '/dashboard': 'dashboard',
      '/kunden': 'kunden',
      '/lieferorte': 'lieferorte',
      '/standorte': 'standorte',
      '/tueren': 'tueren',
      '/pruefungen': 'pruefungen',
      '/reparaturen': 'reparaturen',
      '/berichte': 'berichte',
      '/nutzer': 'nutzer',
      '/sync': 'sync',
      '/einstellungen': 'einstellungen'
    };

    const module = pathToModule[path] || 'dashboard';
    return hasPermission(module);
  }, [hasPermission]);

  
  // 🔧 SYSTEMISCHE ROLE-VALIDIERUNG (regelmäßig DB prüfen)
  const validateUserRole = useCallback(async () => {
    if (!user) return;
    
    try {
      const response = await fetch('/api/auth/validate-session', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('inspecdoor_session')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.user) {
          // Prüfe ob Rolle sich geändert hat
          if (data.user.role !== user.role) {
            console.log('🔄 ROLLE GEÄNDERT:', user.role, '→', data.user.role);
            
            // Update User mit neuer Rolle
            const updatedUser = { ...user, ...data.user };
            setUser(updatedUser);
            updatePermissions(data.user.role);
            
            // Update localStorage
            localStorage.setItem('inspecdoor_user', JSON.stringify(updatedUser));
            
            // User informieren
            alert(`Ihre Rolle wurde geändert von "${user.role}" zu "${data.user.role}". Die Seite wird neu geladen.`);
            window.location.reload();
          }
        }
      }
    } catch (error) {
      console.error('Role validation error:', error);
    }
  }, [user, updatePermissions]);

  // Role-Validierung alle 5 Minuten
  useEffect(() => {
    if (user && !isLoading) {
      const interval = setInterval(validateUserRole, 5 * 60 * 1000);
      return () => clearInterval(interval);
    }
  }, [user, isLoading, validateUserRole]);

  const saveSession = useCallback((userData: FrontendUser, sessionToken: string) => {
    setUser(userData);
    updatePermissions(userData.role);
    
    localStorage.setItem('inspecdoor_user', JSON.stringify(userData));
    localStorage.setItem('inspecdoor_session', sessionToken);
    localStorage.setItem('inspecdoor_session_expires', (Date.now() + 8 * 60 * 60 * 1000).toString());
  }, [updatePermissions]);

  const clearSession = useCallback(() => {
    setUser(null);
    setPermissions(ROLE_PERMISSIONS.monteur);
    
    localStorage.removeItem('inspecdoor_user');
    localStorage.removeItem('inspecdoor_session');
    localStorage.removeItem('inspecdoor_session_expires');
  }, []);

  const loadStoredSession = useCallback(async () => {
    try {
      const storedUser = localStorage.getItem('inspecdoor_user');
      const storedSession = localStorage.getItem('inspecdoor_session');
      const storedExpires = localStorage.getItem('inspecdoor_session_expires');

      if (storedUser && storedSession && storedExpires) {
        const expiresAt = parseInt(storedExpires);
        
        if (Date.now() < expiresAt) {
          const userData: FrontendUser = JSON.parse(storedUser);
          setUser(userData);
          updatePermissions(userData.role);
          
          if (expiresAt - Date.now() < 60 * 60 * 1000) {
            await refreshSession();
          }
        } else {
          clearSession();
        }
      }
    } catch (error) {
      console.error('Error loading stored session:', error);
      clearSession();
    } finally {
      setIsLoading(false);
    }
  }, [updatePermissions, clearSession]);

  const login = useCallback(async (credentials: LoginRequest): Promise<LoginResponse> => {
    setIsLoading(true);
    
    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(credentials),
      });

      const data: LoginResponse = await response.json();

      if (data.success && data.user && data.session) {
        const frontendUser: FrontendUser = {
          id: data.user.id,
          username: data.user.username,
          email: data.user.email,
          full_name: data.user.full_name,
          role: data.user.role,
          is_active: data.user.is_active,
          must_change_password: data.user.must_change_password,
          failed_login_attempts: data.user.failed_login_attempts,
          account_locked_until: data.user.account_locked_until,
          last_login: data.user.last_login,
          last_password_change: data.user.last_password_change,
          created_at: data.user.created_at,
          updated_at: data.user.updated_at
        };

        saveSession(frontendUser, data.session.session_token);
      }

      return data;
    } catch (error) {
      console.error('Login error:', error);
      return {
        success: false,
        message: 'Netzwerkfehler bei der Anmeldung'
      };
    } finally {
      setIsLoading(false);
    }
  }, [saveSession]);

  const logout = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    
    try {
      const sessionToken = localStorage.getItem('inspecdoor_session');
      
      if (sessionToken) {
        await fetch('/api/auth/logout', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${sessionToken}`
          },
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      clearSession();
      setIsLoading(false);
    }
  }, [clearSession]);

  const changePassword = useCallback(async (request: ChangePasswordRequest): Promise<ChangePasswordResponse> => {
    const sessionToken = localStorage.getItem('inspecdoor_session');
    
    if (!sessionToken) {
      return {
        success: false,
        message: 'Keine aktive Session'
      };
    }

    try {
      const response = await fetch('/api/auth/change-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionToken}`
        },
        body: JSON.stringify(request),
      });

      const data: ChangePasswordResponse = await response.json();

      if (data.success && user) {
        const updatedUser = {
          ...user,
          must_change_password: false,
          last_password_change: new Date().toISOString()
        };
        
        setUser(updatedUser);
        localStorage.setItem('inspecdoor_user', JSON.stringify(updatedUser));
      }

      return data;
    } catch (error) {
      console.error('Change password error:', error);
      return {
        success: false,
        message: 'Netzwerkfehler beim Passwort ändern'
      };
    }
  }, [user]);

  const refreshSession = useCallback(async (): Promise<void> => {
    const sessionToken = localStorage.getItem('inspecdoor_session');
    
    if (!sessionToken) return;

    try {
      const response = await fetch('/api/auth/refresh', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${sessionToken}`
        },
      });

      if (response.ok) {
        const data = await response.json();
        
        if (data.success && data.user) {
          const frontendUser: FrontendUser = data.user;
          saveSession(frontendUser, sessionToken);
        }
      } else {
        clearSession();
      }
    } catch (error) {
      console.error('Refresh session error:', error);
      clearSession();
    }
  }, [saveSession, clearSession]);

  useEffect(() => {
    loadStoredSession();
  }, [loadStoredSession]);

  useEffect(() => {
    if (isAuthenticated) {
      const interval = setInterval(refreshSession, 30 * 60 * 1000);
      return () => clearInterval(interval);
    }
  }, [isAuthenticated, refreshSession]);

  const contextValue: AuthContextType = {
    user,
    isLoading,
    isAuthenticated,
    permissions,
    login,
    logout,
    changePassword,
    refreshSession,
    hasPermission,
    hasRole,
    canAccess,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  
  return context;
};

export const useProtectedRoute = (requiredRole?: UserRole, requiredModule?: InspecDoorModule) => {
  const { user, isLoading, hasRole, hasPermission } = useAuth();
  
  const isAuthorized = React.useMemo(() => {
    if (!user || isLoading) return false;
    
    if (requiredRole && !hasRole(requiredRole)) return false;
    if (requiredModule && !hasPermission(requiredModule)) return false;
    
    return true;
  }, [user, isLoading, requiredRole, requiredModule, hasRole, hasPermission]);
  
  return {
    user,
    isLoading,
    isAuthorized,
    isAuthenticated: !!user
  };
};
